import * as React from "react";
import { useTranslation } from "react-i18next";

import { LinkTo } from "@/components/icons/link-to";
import { Card } from "@/components/shared/card";
import useAnalytics from "@/hooks/use-analytics";

import { Button } from "../button";
import { Link } from "../link";

export type EmptyStateCardProps = {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  mainEmptyText: string;
  subEmptyText: string;
  buttonText: string;
  href: string;
  onImportClick: () => void;
};

export default function EmptyStateCard(props: EmptyStateCardProps) {
  const linkRef = React.useRef<HTMLAnchorElement | null>(null);
  const { logAnalytics } = useAnalytics();
  const { t } = useTranslation();

  React.useEffect(() => {
    function handleOpenDashboardClick() {
      logAnalytics("CANVAS_EDITOR_PREFERENCE_PANEL_BUTTON", {
        button_text: "open_in_dashboard",
      });
    }

    const button = linkRef.current;

    if (button) {
      button.addEventListener("click", handleOpenDashboardClick);
    }

    return () => {
      if (button) {
        button.removeEventListener("click", handleOpenDashboardClick);
      }
    };
  }, [logAnalytics]);

  return (
    <Card data-testid="empty-state-card">
      <div className="flex flex-col items-center">
        <props.icon
          className="size-6 text-blue-500"
          data-testid="empty-state-card-icon"
        />
        {props.mainEmptyText && (
          <p className="mb-2 mt-4 text-center text-sm font-medium leading-6 text-gray-800">
            {props.mainEmptyText}
          </p>
        )}
        {props.subEmptyText && (
          <p className="mb-4 text-center text-xxs font-medium leading-4 text-gray-600">
            {props.subEmptyText}
          </p>
        )}
        <Link
          href={props.href}
          target="_blank"
          rel="noreferrer noopener"
          size="small"
          ref={linkRef}
          data-testid="dashboard-button"
          className="w-fit px-7"
        >
          {t("preference-panel.edit-in-dashboard")}
          <LinkTo className="ml-4 size-6 text-white" />
        </Link>
        <Button
          onClick={props.onImportClick}
          rel="noreferrer noopener"
          isInline
          variant="outlined"
          size="small"
          className="mt-4 w-fit px-7"
        >
          {props.buttonText}
        </Button>
      </div>
    </Card>
  );
}
